import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { chartHelper } from '@builttocreate/engine-utils';

import FieldTitle from '../FieldTitle';
import FieldDecorators from '../FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from '../FieldHeader';
import ChartFieldLine from './ChartFieldLine';
import Chart from './Chart';
import ChartModal from './ChartModal';
import Tooltip from '../../../misc/Tooltip';
import Button from '../../../misc/Button';

import ThemeIcons from '../../../../theme/Icons';
import FieldModes from '../../../../constants/FieldModes';

const { getDefaultLine, getDefaultLinePoint } = chartHelper;

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  #chart-field-edit {
    display: ${(props) => (!props.autoShow ? 'none' : 'block')};
  }

  &:hover {
    #chart-field-edit {
      display: block;
    }
  }

`;

const StyledInputGroup = styled.div`
  display: inline-block;
  overflow: auto;
`;

const StyledRowWrapper = styled.div`
  margin-bottom: 8px;
`;

const StyledChartWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;
`;

const StyledSaveButton = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right:0;
  top:0;
  z-index:9999999999999;
`;

class ChartField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,
    value: PropTypes.array, //Lines data
    yTitle: PropTypes.string,
    yMin: PropTypes.number,
    yMax: PropTypes.number,
    xTitle: PropTypes.string,
    xMin: PropTypes.number,
    xMax: PropTypes.number,

    primaryDisplayOnly: PropTypes.bool, //Used to hide line forms
    primaryMaxHeight: PropTypes.number, //Used to set max height of graph chart

    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Cell styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Row Action Handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    title: PropTypes.string,
    tipVisible: PropTypes.bool,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,

    //Used for calculating width and height of element
    onMeasureCompleted: PropTypes.func

  }

  static defaultProps = {
    value: []
  }

  constructor(props) {
    super(props);
    this.state = { displayModal: false };
  }

  componentDidUpdate(prevProps) {

    const { wrapperRef } = this.state;

    const { fieldId, fieldIdentifier, fieldPositionId, onMeasureCompleted, measure, measuredHeight, measuredOn, updatedOn } = this.props;

    const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);

    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && measure && onMeasureCompleted && refHeightAvailable && remeasureRequired) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
      });
    }

  }

  render() {

    const { displayModal, wrapperRef } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      disabled,
      isMobileView,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      yTitle,
      yMin,
      yMax,
      xTitle,
      xMin,
      xMax,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleFontStyle,
      titleFontWeight,
      titleTextAlign,
      titleTextTransform,
      titleTextDecoration,

      //Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
      margin,

      primaryDisplayOnly,
      primaryMaxHeight,

      onMeasureCompleted,

      onFocus,
      onBlur,
      decorators,
      onDecoratorClick,
      fieldMode,

    } = this.props;

    const lineFormTheme = {
      primaryColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
    };

    return (
      <StyledWrapper
        {...{ margin }}
        autoShow={isMobileView}
        data-testid={`${fieldPositionId}-chart-field`}
        ref={(e) => {
          if (e && !wrapperRef) this.setState({ wrapperRef: e });
        }}
      >
        <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
        {this.getTooltip()}
        <StyledChartWrapper>
          <Chart
            {...{
              id: fieldPositionId,
              yTitle,
              yMin: parseFloat(yMin),
              yMax: parseFloat(yMax),
              xTitle,
              xMin: parseFloat(xMin),
              xMax: parseFloat(xMax),
              lines: this.filterEmptyPoints(value),
              backgroundColor,
              borderColor,
              borderWidth,
              borderRadius,
              primaryMaxHeight,
              fieldId,
              tipVisible,
              tipTitle,
              tipDescription,
              disabled,
            }}
          />
          {disabled ? null : (
            <StyledSaveButton>
              <Button
                solid
                size="sm"
                icon={ThemeIcons.fields.chart}
                text="Edit"
                onClick={() => {

                  this.setState({ displayModal: !displayModal });

                  if (onFocus) {

                    onFocus(fieldId, fieldIdentifier, fieldPositionId,
                      {
                        blur: () => {

                          this.setState({ displayModal: false });
                          /**
                           * Generally, when user closes the chart modal or clicks on close icon
                           * onHide handler in chart modal will be triggered which could fire off onBlur
                           * Since there is no way to invoke onHide, we fire off onBlur here
                           */
                          if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

                        }
                      });

                  }

                }}
              />
            </StyledSaveButton>
          )}
        </StyledChartWrapper>
        {primaryDisplayOnly ? null : (
          <StyledInputGroup data-testid={`${fieldPositionId}-chart-field-lines`}>
            {value.map((line, lineIndex) => (
              <StyledRowWrapper key={`${line._id}-${lineIndex}`}>
                <ChartFieldLine
                  id={`${fieldPositionId}-${line._id}`}
                  disabled={disabled}
                  yTitle={yTitle}
                  xTitle={xTitle}
                  lineId={line._id}
                  lineIndex={lineIndex}
                  title={line.title}
                  description={line.description}
                  points={this.normalizePoints(line.points)}
                  {...lineFormTheme}
                  onDelete={this.handleLineDelete}
                  onChange={this.handleLineChange}
                  onPointCreate={this.handlePointCreate}
                  onPointChange={this.handlePointChange}
                  onPointDelete={this.handlePointDelete}
                  displayModal={displayModal}
                />
              </StyledRowWrapper>
            ))}
          </StyledInputGroup>
        )}
        <ChartModal
          id={`${fieldPositionId}-modal`}
          display={displayModal}
          onHide={() => {

            this.setState({ displayModal: false });

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

          }}
          width={isMobileView ? 290 : null}
          theme={lineFormTheme}
          {...{
            yTitle,
            yMin,
            yMax,
            xTitle,
            xMin,
            xMax,
            value
          }}
          onChange={this.handleChange}
          onLineCreate={this.handleLineCreate}
          onLineDelete={this.handleLineDelete}
          onLineChange={this.handleLineChange}
          onPointCreate={this.handlePointCreate}
          onPointChange={this.handlePointChange}
          onPointDelete={this.handlePointDelete}
        />
      </StyledWrapper>
    );

  }

  filterEmptyPoints = (lines) => {

    return (lines || []).map(line => ({
      ...line,
      points: (line.points || []).filter(p =>
        typeof p.x === 'number' && Number.isFinite(p.x)
        && typeof p.y === 'number' && Number.isFinite(p.y)
      )
    }));
    
  }

  normalizePoints = (points) => {
    return (points || []).map(point => ({
      ...point,
      x: point.hasOwnProperty('x') ? point.x : null,
      y: point.hasOwnProperty('y') ? point.y : null,
    }));
  }

  cleanLines = (lines) => {
    return lines.map(line => ({
      ...line,
      points: (line.points || []).map(point => {
        const cleaned = { ...point };
        if (cleaned.x === null || cleaned.x === undefined) delete cleaned.x;
        if (cleaned.y === null || cleaned.y === undefined) delete cleaned.y;
        return cleaned;
      })
    }));
  }

  getTooltip = () => {

    const { fieldId, tipVisible, tipTitle, tipDescription, disabled } = this.props;
    if (disabled) return null;

    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );

  };

  handleChange = (changes) => {
    const { fieldId, fieldIdentifier, fieldPositionId, onChange } = this.props;
    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
  }

  handleLineDelete = (lineId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const lineIndex = value.findIndex((line) => line._id === lineId);

    const nextLines = [...value];

    if (value.length > 1) {

      nextLines.splice(lineIndex, 1);
      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

    }

  }

  handleLineChange = (lineId, field, change) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const lineIndex = value.findIndex((line) => line._id === lineId);
    const nextLines = [...value];

    nextLines[lineIndex] = { ...nextLines[lineIndex], [field]: change };

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

  }

  handleLineCreate = () => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const nextLines = [...value];
    nextLines.push(getDefaultLine());

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

  }

  handlePointCreate = (lineId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const lineIndex = value.findIndex((line) => line._id === lineId);
    const nextLines = [...value];

    const line = nextLines[lineIndex];
    nextLines[lineIndex] = { ...line, points: [...line.points] };
    nextLines[lineIndex].points.push(getDefaultLinePoint());

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

  }

  handlePointChange = (lineId, pointId, field, change) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const nextLines = [...value];

    const lineIndex = nextLines.findIndex((line) => line._id === lineId);
    const nextLine = { ...nextLines[lineIndex], points: [...nextLines[lineIndex].points] };

    const pointIndex = nextLine.points.findIndex((point) => point._id === pointId);
    nextLine.points[pointIndex] = { ...nextLine.points[pointIndex], [field]: change };

    nextLines[lineIndex] = nextLine;

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

  }

  handlePointDelete = (lineId, pointId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, value, onChange } = this.props;

    const nextLines = [...value];

    const lineIndex = nextLines.findIndex((line) => line._id === lineId);
    const nextLine = { ...nextLines[lineIndex], points: [...nextLines[lineIndex].points] };

    const pointIndex = nextLine.points.findIndex((point) => point._id === pointId);

    //Must always have at least 1 point.
    //Victory chart throws an error if you have no point data.
    if (nextLine.points.length > 1) {

      nextLine.points.splice(pointIndex, 1);
      nextLines[lineIndex] = nextLine;

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, this.cleanLines(nextLines));

    }

  }
}

export default ChartField;
